/**
 * @author Petr Mikušek <petr.mikusek@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-disable no-bitwise */

import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';
import pick from 'lodash/pick';

import ScheduleEmpty from './schedule-empty';
import ScheduleDeterministic from './schedule-deterministic';
import ScheduleMedium from './schedule-medium';
import SchedulePlaylist from './schedule-playlist';

// TODO #138 Zavést registry pro detekce strategií?
/**
 * Utilitní třída pro vytvoření strategie přehrávání z obsahu.
 */
class ScheduleContent {
  /**
   * Detekuje strategii pro (ne)polymorfní obsah a vytvoří její instanci.
   * @param {Content} content (Ne)polymorfní obsah.
   * @param {ContentInfo} contentInfo Informace o obsahu zařízení.
   * @param {ViewerRegistry} viewerRegistry Registr informací o typech médií.
   * @returns {ScheduleStrategy} Detekovaná strategie plánování.
   * @throws {Error} (Ne)polymorfní obsah má více typů obsahu zároveň.
   */
  static detectStrategy(content, contentInfo, viewerRegistry) {
    switch (ScheduleContent.type(content)) {
      case ContentType.Empty:
        return new ScheduleEmpty();
      case ContentType.Layout:
        // TODO #458 Vnitřní šablony nyní neumíme, ale ScheduleContent.detectStrategy to ošetřuje.
        // Po zprovoznění vnitřních šablon nahradit za return new ScheduleLayout(...).
        return new ScheduleEmpty();
      case ContentType.Medium:
        return new ScheduleMedium(
          contentInfo.session,
          content.medium,
          viewerRegistry
        );
      case ContentType.Playlist:
        if (content.playlist.deterministic) {
          return new ScheduleDeterministic(
            contentInfo.session,
            content.playlist,
            viewerRegistry
          );
        }
        return new SchedulePlaylist(
          contentInfo,
          content.playlist,
          viewerRegistry
        );
      // Kvůli uspokojení eslint pravidla default-case.
      default:
        throw ScheduleContent.errorDefaultCase(content);
    }
  }

  // TODO: Nebo vlastní typ výjimky?
  /**
   * Vytvoří výjimku pro neočekávaný default case při úplném pokrytí ve switch
   * podle ScheduleContent.type.
   * @param {Content} content (Ne)polymorfní obsah.
   * @returns {Error} Programátorská (nikoliv uživatelská) chyba/assert.
   */
  static errorDefaultCase(content) {
    return new Error(`Unexpected default case for content ${content}.`);
  }

  // TODO: Nebo vlastní typ výjimky?
  /**
   * Vytvoří výjimku při více typech (ne)polymorfního obsahu současně .
   * @param {Content} content (Ne)polymorfní obsah.
   * @returns {Error} (Ne)polymorfní obsah má více typů obsahu zároveň.
   */
  static errorMultipleContentTypes(content) {
    throw new Error(`Multiple content types for content ${content}.`);
  }

  /**
   * Vrátí řetězec obsahující typ a ID obsahu.
   * @param {Content} content (Ne)polymorfní obsah.
   * @returns {string} Řetězec obsahující typ a ID obsahu nebo {} pro prázdný obsah.
   */
  static toString(content) {
    return JSON.stringify(
      pick(content, 'medium.id', 'layout.id', 'playlist.id')
    );
  }

  /**
   * Zjistí právě jeden (i prázdný) typ obsahu z (ne)polymorfního obsahu.
   * @param {Content} content
   * @returns {ContentType} Právě jeden (i prázdný) typ obsahu nebo vyhodí chybu.
   * @throws {Error} (Ne)polymorfní obsah má více typů obsahu zároveň.
   * @throws {TypeError} (Ne)polymorfní obsah není objekt.
   */
  static type(content) {
    // TODO: Je potřeba explicitní kontrola? Asi ano pro detekci předání null
    // nebo jiného typu, kde by se mylně vyhodnotila jako empty content.
    // Anti-pattern, toto je assert pro vývoj než nějaká nutná runtime validace.
    if (!isObject(content)) {
      throw new TypeError(`Invalid content ${content}.`);
    }

    const { layout, medium, playlist } = content;
    let t = 0 | 0;
    t += !isNil(layout) << 0;
    t += !isNil(medium) << 1;
    t += !isNil(playlist) << 2;

    // Pokud je t mocnina 2 (včetně 0), tak byl přítomen právě jeden typ obsahu
    // nebo žádný (prázdný obsah).
    // TODO: Zvážit ještě "and trick", aby i výpočet podmínky byl branch-less:
    // https://www.geeksforgeeks.org/program-to-find-whether-a-given-number-is-power-of-2/
    if (t === 0 || (t & (t - 1)) === 0) {
      return t;
    }

    throw ScheduleContent.errorMultipleContentTypes(content);
  }
}

/**
 * Číselník typů (ne)polymorfního obsahu. Pro správné fungování
 * {@link ScheduleContent.type} nutné udržovat hodnoty jako mocniny 2.
 * @enum {number}
 */
export const ContentType = {
  Empty: 0 | 0,
  Layout: 1 << 0,
  Medium: 1 << 1,
  Playlist: 1 << 2
};

export default ScheduleContent;
